import { useMemo, useState, type ReactNode } from 'react'
import { useT } from '@/i18n'
import { uiMessages } from '@/i18n/messages/ui'

export interface Column<T> {
  key: string
  header: string
  align?: 'left' | 'right'
  /** Return a comparable value to make the column sortable; omit for a non-sortable column. */
  sortValue?: (row: T) => string | number
  render: (row: T) => ReactNode
  className?: string
  /** Pin the column to the right so an action like Edit stays on screen. */
  sticky?: 'right'
}

/**
 * A compact table with row numbering (#) and click-to-sort headers — the house default for
 * every table. Sort state is internal; pass `initialSort` to pick the opening order.
 */
export function SortableTable<T>({
  columns, rows, rowKey, initialSort, empty, fixed,
}: {
  columns: Column<T>[]
  rows: T[]
  rowKey: (row: T, index: number) => string
  initialSort?: { key: string; dir: 'asc' | 'desc' }
  empty?: ReactNode
  /** Columns share the table width and wrap instead of forcing a horizontal scroll. */
  fixed?: boolean
}) {
  const t = useT(uiMessages)
  const [sort, setSort] = useState<{ key: string; dir: 'asc' | 'desc' } | null>(initialSort ?? null)

  const sorted = useMemo(() => {
    if (!sort) return rows
    const col = columns.find((c) => c.key === sort.key)
    if (!col?.sortValue) return rows
    const dir = sort.dir === 'asc' ? 1 : -1
    return [...rows].sort((a, b) => {
      const av = col.sortValue!(a)
      const bv = col.sortValue!(b)
      const r = typeof av === 'number' && typeof bv === 'number'
        ? av - bv
        : String(av).localeCompare(String(bv))
      return r * dir
    })
  }, [rows, sort, columns])

  const toggle = (key: string) =>
    setSort((s) => (s?.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }))

  const sticky = (c: Column<T>) =>
    c.sticky === 'right'
      ? 'sticky right-0 z-10 bg-white group-hover:bg-neutral-50 dark:bg-neutral-900 dark:group-hover:bg-neutral-800/40'
      : ''

  return (
    <div className={`${fixed ? 'overflow-hidden' : 'overflow-x-auto'} rounded-2xl bg-white ring-1 ring-neutral-200 dark:bg-neutral-900 dark:ring-neutral-800`}>
      <table className={`w-full text-sm ${fixed ? 'table-fixed' : ''}`}>
        <thead>
          <tr className="border-b border-neutral-200 text-left text-xs text-neutral-500 dark:border-neutral-800">
            <th className="w-10 px-2 py-2 text-right font-medium sm:px-3">#</th>
            {columns.map((c) => {
              const active = sort?.key === c.key
              const right = c.align === 'right'
              return (
                <th key={c.key} className={`px-2 py-2 font-medium sm:px-3 ${right ? 'text-right' : ''} ${sticky(c)} ${c.className ?? ''}`}>
                  {c.sortValue ? (
                    <button
                      type="button"
                      onClick={() => toggle(c.key)}
                      className={`inline-flex items-center gap-1 hover:text-neutral-800 dark:hover:text-neutral-200 ${active ? 'text-neutral-800 dark:text-neutral-200' : ''}`}
                    >
                      {c.header}
                      <span aria-hidden className="text-[10px]">{active ? (sort!.dir === 'asc' ? '▲' : '▼') : '↕'}</span>
                    </button>
                  ) : (
                    c.header
                  )}
                </th>
              )
            })}
          </tr>
        </thead>
        <tbody>
          {sorted.map((row, i) => (
            <tr
              key={rowKey(row, i)}
              className="group border-b border-neutral-100 last:border-0 hover:bg-neutral-50 dark:border-neutral-800/60 dark:hover:bg-neutral-800/40"
            >
              <td className="tabular px-2 py-2 text-right text-xs text-neutral-400 sm:px-3">{i + 1}</td>
              {columns.map((c) => (
                <td key={c.key} className={`px-2 py-2 sm:px-3 ${c.align === 'right' ? 'text-right tabular' : ''} ${sticky(c)} ${c.className ?? ''}`}>
                  {c.render(row)}
                </td>
              ))}
            </tr>
          ))}
          {sorted.length === 0 && (
            <tr>
              <td colSpan={columns.length + 1} className="px-3 py-10 text-center text-sm text-neutral-500">
                {empty ?? t('nothingToShow')}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  )
}
